import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, LINKS } from "@/lib/site";
import { Cosmos } from "@/components/site/Cosmos";
import { PlaybookGate } from "@/components/site/PlaybookGate";
import "./FinalSections.css";

const inputCls =
  "bg-transparent border-0 border-b border-white/60 rounded-none h-12 px-0 text-white placeholder:text-white font-light focus-visible:ring-0 focus-visible:border-flame text-base";

export const FinalCta = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { amount: .1 });
  const reducedMotion = useReducedMotion();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Got it. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="contact-cosmos relative overflow-hidden bg-ink text-paper border-t border-ink" data-testid="contact-section">
      <Cosmos vivid paused={editing || reducedMotion || !inView} />
      <div className="contact-layout">
        <div className="contact-copy">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-display font-semibold tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl leading-[0.98] text-white text-balance">
              Find what's holding back{" "}
              <span className="font-editorial italic font-medium text-cobalt">your sales.</span>
            </h2>
            <p className="mt-6 text-xl font-light leading-relaxed text-white max-w-md text-pretty">
              In 30 minutes, we'll show you what we'd fix, what we'd take over and what it costs.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 sm:gap-4">
              <a
                href={LINKS.cal}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex w-full sm:w-auto min-h-[3.25rem] items-center justify-center gap-2 bg-flame px-6 sm:px-7 py-3.5 font-mono text-base font-bold uppercase tracking-[0.1em] sm:tracking-[0.12em] text-ink border border-flame transition-all duration-200 hover:-translate-y-1 hover:shadow-hard-blue active:translate-y-0 active:shadow-none"
                data-testid="final-book"
              >
                Book a meeting
                <ArrowUpRight size={18} strokeWidth={2.5} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <PlaybookGate
                triggerClassName="group inline-flex w-full sm:w-auto min-h-[3.25rem] items-center justify-center gap-2 border border-white/45 px-6 sm:px-7 py-3.5 font-mono text-base uppercase tracking-[0.1em] sm:tracking-[0.12em] text-white transition-colors duration-200 hover:bg-white hover:text-ink"
                label="Free $1M playbook"
                showArrow={false}
                dataTestId="final-playbook"
              />
            </div>
          </motion.div>
        </div>

        <div className="contact-glass">
          <p className="text-lg text-white mb-6 text-pretty">Or drop your details</p>
          <form onSubmit={submit} onFocusCapture={() => setEditing(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setEditing(false); }} className="space-y-6" noValidate data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <label className="block">
                <span className="block text-base font-medium">Your name</span>
                <Input autoComplete="name" placeholder="Your name" value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name" />
              </label>
              <label className="block">
                <span className="block text-base font-medium">Email</span>
                <Input type="email" autoComplete="email" placeholder="you@company.com" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email" />
              </label>
            </div>
            <label className="block">
              <span className="block text-base font-medium">Company (optional)</span>
              <Input autoComplete="organization" placeholder="Company name" value={form.company} onChange={update("company")} className={inputCls} data-testid="contact-company" />
            </label>
            <label className="block">
              <span className="block text-base font-medium mb-2">What would you like to improve?</span>
              <Textarea
                placeholder="What do you sell? Where are you stuck?"
                value={form.message}
                onChange={update("message")}
                rows={3}
                className="bg-transparent border-0 border-b border-white/60 rounded-none px-0 text-white placeholder:text-white font-light focus-visible:ring-0 focus-visible:border-flame resize-none text-base"
                data-testid="contact-message"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-2 bg-cobalt px-8 py-4 font-mono text-base font-medium uppercase tracking-[0.12em] text-white border border-cobalt transition-all duration-200 hover:bg-cobalt-hover active:scale-95 disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? "Sending..." : "Send message"}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <p className="font-mono text-base font-medium uppercase tracking-[0.1em] text-white text-pretty" data-testid="form-reassurance">
              We reply within 1 working day • No spam, ever
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
